import React, { useMemo } from 'react';
import { VictoryArea, VictoryLine, DomainTuple } from 'victory';
import { VictoryLabel } from 'victory-core';

import { formatPrice } from '../../../../helpers/NumberFormatter';

import { colors } from '../../../../colors';
import { PriceChartApiResponse } from '../../../../screens/Detail/Charts';
import { Bot } from 'grts-utils/types/Bot';

type DataPoint = {
  low: number;
  open: number;
  close: number;
  high: number;
  x: number;
  x2: number;
};

interface Props {
  candleResponse: PriceChartApiResponse | undefined;
  bot?: Bot | undefined;
  data: DataPoint[];
  domain:
    | {
        x: DomainTuple;
        y: DomainTuple;
      }
    | undefined;
  handleZoomIn: () => Promise<void>;
  temporaryCandles: PriceChartApiResponse | undefined;
}

const cbrtGray = 'rgb(130, 130, 130)';

const CbrtRangeChart: React.FC<Props> = (props: Props) => {
  const { data, domain, candleResponse, bot, temporaryCandles } = props;

  const cbrt = bot?.complexParams?.cbrt;
  const latestOrder = bot?.runtime?.latestOrder;
  const cbrtRange = bot?.runtime?.cbrtRange;

  const cbrtOpen = useMemo(() => {
    if (cbrtRange?.open) {
      return cbrtRange.open;
    }

    /**
     * We'll start at the end
     * We're looking for the start of new trend
     * For the open of the wave
     */
    if (!candleResponse?.heikin || candleResponse.heikin.length === 0) {
      return null;
    }

    let open;

    for (let i = candleResponse.heikin.length - 1; i >= 0; i--) {
      const candle = candleResponse.heikin[i];

      if (latestOrder === 'SELL' && candle.direction === 'down') {
        open = (candle.open + candle.close) / 2;
        break;
      }

      if (latestOrder === 'BUY' && candle.direction === 'up') {
        open = (candle.open + candle.close) / 2;
        break;
      }

      if (candle.startOfNewTrend) {
        open = candle.open;
        break;
      }
    }

    return open;
  }, [cbrtRange?.open, candleResponse?.heikin, latestOrder]);

  const cbrtClose = useMemo(() => {
    if (!cbrt || !cbrtOpen || !candleResponse?.heikin) {
      return null;
    }

    const cbrtDirection = cbrtRange?.direction
      ? cbrtRange.direction === 'up'
        ? 1
        : -1
      : latestOrder
      ? latestOrder === 'BUY'
        ? -1
        : 1
      : candleResponse.heikin[candleResponse.heikin.length - 1].direction ===
        'down'
      ? -1
      : 1;

    return cbrtDirection * cbrt * cbrtOpen + cbrtOpen;
  }, [
    candleResponse?.heikin,
    cbrt,
    cbrtOpen,
    cbrtRange?.direction,
    latestOrder,
  ]);

  const xStartOfCbrtLine = useMemo(() => {
    if (cbrtRange?.timestamp) {
      return cbrtRange?.timestamp;
    }

    if (!data || data.length === 0 || !cbrtClose) {
      return null;
    }

    let timestamp;

    for (let i = data.length - 1; i > 0; i--) {
      const dataPoint = data[i];

      if (dataPoint.high >= cbrtClose && cbrtClose >= dataPoint.low) {
        timestamp = dataPoint.x;
        break;
      }
    }

    return timestamp;
  }, [cbrtRange?.timestamp, cbrtClose, data]);

  const cbrtDataPoints = useMemo(() => {
    if (!cbrt || !data?.length || !candleResponse?.heikin?.length) {
      return null;
    }

    /** We'll need the open of the bot or manually calculatedd */
    const close = cbrtClose;

    if (!close || !cbrtOpen) {
      return [];
    }

    /** We need to calculate where to start the line
     * When we get data via the bot, we already know the data (might need small adjustment for the chart)
     */

    let xDomainMin = xStartOfCbrtLine;

    if (xDomainMin === undefined) {
      xDomainMin = domain?.x[0] as number;
    }
    const precision =
      close.toFixed(0).length > 2 ? 0 : close.toFixed(0).length > 1 ? 2 : 7;
    const currentPrice = data[data.length - 1].close;
    const latestHeikinCandle =
      candleResponse.heikin[candleResponse.heikin.length - 1];

    let label;

    if (
      (latestOrder === 'BUY' && latestHeikinCandle.direction === 'up') ||
      (latestOrder === 'SELL' && latestHeikinCandle.direction === 'down') ||
      Math.abs(currentPrice - cbrtOpen) < Math.abs(cbrtClose - cbrtOpen)
    ) {
      label = `cbrt: ${formatPrice(close, true, precision)}`;
    } else {
      label = 'Ready to order...';
    }

    const dataPoints = [
      { x: xDomainMin, y: close, label: '' },
      {
        x: domain?.x[1],
        y: close,
        label,
      },
    ];

    return dataPoints;
  }, [
    candleResponse?.heikin,
    cbrt,
    cbrtClose,
    cbrtOpen,
    data,
    domain?.x,
    latestOrder,
    xStartOfCbrtLine,
  ]);

  const cbrtOpenDataPoints = useMemo(() => {
    if (!cbrt || !cbrtRange || !Object.keys(cbrtRange).length) {
      return null;
    }

    const open = cbrtOpen;

    if (!open) {
      return [];
    }

    /** We need to calculate where to start the line
     * When we get data via the bot, we already know the data (might need small adjustment for the chart)
     */

    const xDomainMin = xStartOfCbrtLine;

    const dataPoints = [
      { x: xDomainMin, y: open, label: '' },
      {
        x: domain?.x[1],
        y: open,
        label: '',
      },
    ];

    return dataPoints;
  }, [cbrt, cbrtOpen, cbrtRange, domain?.x, xStartOfCbrtLine]);

  const reverseCbrtClose = useMemo(() => {
    if (!cbrt || !cbrtRange || !Object.keys(cbrtRange).length || !cbrtOpen) {
      return null;
    }

    const cbrtDirection = cbrtRange.direction === 'up' ? -1 : 1;

    const close = cbrtDirection * cbrt * cbrtOpen + cbrtOpen;
    return close;
  }, [cbrt, cbrtOpen, cbrtRange]);

  const reverseCbrtDataPoints = useMemo(() => {
    if (
      !domain ||
      !cbrt ||
      !cbrtRange ||
      !Object.keys(cbrtRange).length ||
      !cbrtOpen
    ) {
      return null;
    }

    const close = reverseCbrtClose;

    if (!close) {
      return null;
    }

    /** We need to calculate where to start the line
     * When we get data via the bot, we already know the data (might need small adjustment for the chart)
     */

    let xDomainMin = xStartOfCbrtLine;

    if (xDomainMin === undefined) {
      xDomainMin = domain.x[0] as number;
    }
    const precision =
      close.toFixed(0).length > 2 ? 0 : close.toFixed(0).length > 1 ? 2 : 7;

    const dataPoints = [
      { x: xDomainMin, y: close, label: '' },
      {
        x: domain?.x[1],
        y: close,
        label: `cbrt: ${formatPrice(close, true, precision)}`,
      },
    ];

    return dataPoints;
  }, [cbrt, cbrtOpen, cbrtRange, domain, reverseCbrtClose, xStartOfCbrtLine]);

  const cbrtAreaDataPoints = useMemo(() => {
    return cbrtDataPoints?.map((d) => ({
      x: d.x,
      y: d.y,
      y0: reverseCbrtClose,
    }));
  }, [cbrtDataPoints, reverseCbrtClose]);

  const cbrtLineColor = useMemo(() => {
    if (!data || data.length === 0) {
      return 'rgb(110, 110, 110)';
    }

    const open = cbrtOpen;
    const currentPrice = data[data.length - 1].close;
    const close = cbrtClose;

    if (!close || !open) {
      return 'rgb(110, 110, 110)';
    }

    if (
      !latestOrder ||
      (latestOrder === 'BUY' && currentPrice < close) ||
      (latestOrder === 'SELL' && currentPrice > close)
    ) {
      return 'rgb(110, 110, 110)';
    }

    return colors.gold;
  }, [cbrtClose, cbrtOpen, data, latestOrder]);

  return (
    <g>
      {!!data.length && cbrtAreaDataPoints && (
        <VictoryArea
          {...props}
          data={cbrtAreaDataPoints}
          style={{
            data: {
              fill: '#FFC70035',
            },
          }}
        />
      )}

      {!!data.length && cbrtDataPoints && (
        <VictoryLine
          {...props}
          data={cbrtDataPoints}
          labelComponent={
            <VictoryLabel
              dy={-3}
              dx={-5}
              textAnchor={'end'}
              className={!!temporaryCandles ? '' : 'mrcState-label'}
            />
          }
          style={{
            data: {
              stroke: cbrtLineColor,
              strokeWidth: 2,
            },
            labels: {
              fill: cbrtLineColor,
              fontSize: 12,
              fontWeight: 600,
            },
          }}
        />
      )}

      {!!data.length && cbrtOpenDataPoints && (
        <VictoryLine
          {...props}
          data={cbrtOpenDataPoints}
          style={{
            data: {
              stroke: cbrtGray,
              strokeWidth: 1,
            },
            labels: {
              fill: cbrtGray,
              fontSize: 10,
              fontWeight: 500,
            },
          }}
        />
      )}

      {!!data.length && reverseCbrtDataPoints && (
        <VictoryLine
          {...props}
          data={reverseCbrtDataPoints}
          labelComponent={
            <VictoryLabel
              dy={12}
              dx={-5}
              renderInPortal={true}
              textAnchor={'end'}
            />
          }
          style={{
            data: {
              stroke: cbrtGray,
              strokeWidth: 1,
            },
            labels: {
              fill: cbrtGray,
              fontSize: 10,
              fontWeight: 500,
            },
          }}
        />
      )}
    </g>
  );
};

export default React.memo(CbrtRangeChart);
